import { CaseSchema } from "@api/generated/types.gen";

import { Badge } from "@components/ui/badge";

import { DOCTOR_ACCESS_COPY, doctorAccessState } from "@constants/doctor-access";
import { getServiceTypeLabel } from "@constants/service-types";

import { formatNullableDateToLocal } from "@utils/dateUtils";

import { Column } from "../../DataTables/types/data-table";
import { STATUS_BADGE_VARIANT, STATUS_MAPPING } from "./status";

// Status mapping function to convert backend status to user-friendly labels
const getStatusLabel = (status: string): string => {
  return status in STATUS_MAPPING
    ? STATUS_MAPPING[status as keyof typeof STATUS_MAPPING]
    : status;
};

// Column order (Claim Number, Examinee, [Doctor — inserted by CasesList for
// admins], Service Type, Appointment, Report Due, Status) follows the Report
// Studio design (Claude Design project "Report Studio UI Update", Main Pages
// (Half-Dark).dc.html). Service Type is the admin-facing, coded field (see
// constants/service-types.ts). CasesList swaps this column for CaseTypeColumn
// on the expert (doctor-view) list — same split as the case detail page
// (MDRS-325): the service_type slug means nothing to a doctor, but the
// free-text case_type does.
export const CaseListTableColumns: Column<CaseSchema>[] = [
  {
    id: "claim_number",
    header: "Claim Number",
    // Blue link-styled text (design: Report Studio UI Update, Main Pages
    // (Half-Dark)) — the claim number is the row's primary identifier, and
    // the whole row is already clickable (onRowClick), so this just signals
    // that affordance visually.
    accessor: ({ claim_number }) =>
      claim_number ? (
        <span className="font-semibold text-primary-ink">{claim_number}</span>
      ) : (
        "-"
      ),
    sortable: true,
  },
  {
    id: "examinee_name",
    header: "Examinee Name",
    accessor: ({ examinee_name }) => examinee_name || "-",
    sortable: true,
  },
  {
    id: "service_type",
    header: "Service Type",
    accessor: ({ service_type }) => getServiceTypeLabel(service_type) || "-",
    sortable: true,
    className: "text-foreground-muted",
  },
  {
    id: "appointment_date",
    header: "Appointment Date",
    accessor: ({ appointment_date }) =>
      formatNullableDateToLocal(appointment_date, undefined, "-"),
    sortable: true,
  },
  {
    id: "report_due_date",
    header: "Report Due Date",
    accessor: ({ report_due_date }) =>
      formatNullableDateToLocal(report_due_date, undefined, "-"),
    sortable: true,
  },
  {
    id: "status",
    header: "Status",
    accessor: ({ status }) =>
      status ? (
        <Badge variant={STATUS_BADGE_VARIANT[status] ?? "default"}>
          {getStatusLabel(status)}
        </Badge>
      ) : (
        "-"
      ),
    sortable: true,
  },
];

/**
 * Case Type column — swapped in for Service Type on the expert (doctor-view)
 * case list. case_type is free text (a Vinci scheme name, or admin-typed),
 * not a coded lookup, so it renders as-is with no label mapping.
 */
export const CaseTypeColumn: Column<CaseSchema> = {
  id: "case_type",
  header: "Case Type",
  accessor: ({ case_type }) => case_type || "-",
  sortable: true,
  className: "text-foreground-muted",
};

/**
 * Admin-only Doctor column (MDRS-230). Replaces the earlier Yes/No "Ready for
 * doctor" text: the doctor's name is the useful primary value, with the release
 * state as a sub-line, so one column answers both "whose case is this" and "can
 * they see it". Appended by `CasesList` only for admins.
 *
 * Not sortable: the list isn't server-sorted on this field, and a client-side
 * sort over the current page only would be misleading.
 */
export const DoctorColumn: Column<CaseSchema> = {
  id: "doctor",
  header: "Doctor",
  accessor: ({ doctor_name, is_doctor_ready }) => {
    // No doctor: muted "Not assigned" with no sub-line — there's no release
    // state to report when there's nobody to release to.
    if (!doctor_name) {
      return (
        <span className="text-foreground-muted">
          {DOCTOR_ACCESS_COPY.doctorUnassigned}
        </span>
      );
    }

    return (
      <div className="min-w-0">
        <div className="truncate font-medium">{doctor_name}</div>
        <div className="mt-0.5 text-xs text-foreground-muted">
          {doctorAccessState(true, is_doctor_ready)}
        </div>
      </div>
    );
  },
};
